// AN ID FOR A TREE NODE THAT HAS NONE OF ITS OWN.
//
// Owner, 2026-10-20: "in some cases we might need to generate a unique guid
// because groups don't have one until the jsx tree, and subprojects don't have
// one until the jsx, and a utility helper".
//
// He is right about the data. Only a COMPONENT carries a UniqueId - Revit's.
// A SubProject (a category) and a Group (a family/type) exist only as
// structure in the export, and the id the tree uses for them is `$id`: a
// JSON.NET reference number, assigned by the serializer in document order. So
// an export written by a different path, or a trimmed index, can arrive with
// no `$id` at all - and then every category and group node is built with
// id: undefined. Every one of them collides, the open-node keys become
// "undefined/undefined", and nothing expands.
//
// THE FIX IS NOT uuid(). That is what the tree render already did wrong:
// key={generateId()} called at RENDER time gave every node a new identity on
// every render, so React rebuilt the whole tree and threw away the expansion
// and selection a click had just set. A generated id must be:
//   - assigned ONCE, when the node is built, never during render;
//   - DETERMINISTIC, so the same document builds the same ids every time -
//     otherwise a saved set of open nodes, a scroll target or a selection
//     stops matching the moment the tree is rebuilt.
// So this derives the id from what the node IS - its document, its ancestors
// and its own name - rather than from a random source.

// FNV-1a, 32-bit. Small, dependency-free, and the same function the record
// index already uses for its chunk buckets, so two parts of this codebase
// hash the same way.
function fnv1a32(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/**
 * A stable id for a structural node, from the parts that identify it.
 *
 * Parts are joined with a separator that cannot appear in a Revit category or
 * family name, so "A" + "B/C" and "A/B" + "C" cannot collide.
 */
export function stableNodeId(kind, ...parts) {
  const basis = [String(kind || 'node'), ...parts.map((p) => (p === undefined || p === null ? '' : String(p)))].join('\u0000');
  // Two rounds over different salts: 64 bits of id from a 32-bit hash, which
  // is enough for the tens of thousands of nodes a document has, and cheap.
  const a = fnv1a32(basis).toString(16).padStart(8, '0');
  const b = fnv1a32(`${basis}\u0000salt`).toString(16).padStart(8, '0');
  return `gen-${kind}-${a}${b}`;
}

/**
 * The node's own id when it has one, else a generated, stable one.
 *
 * `own` is whatever the export gave us ($id, UniqueId). Anything empty,
 * null or the STRING "undefined" - which is what template building produces
 * from a missing field, and is worse than nothing because it looks like a
 * value - falls through to the generated id.
 */
export function nodeIdOr(own, kind, ...parts) {
  const v = own === undefined || own === null ? '' : String(own).trim();
  if (v && v !== 'undefined' && v !== 'null') return v;
  return stableNodeId(kind, ...parts);
}

// ONE FILE'S IDS MUST NOT BE ANOTHER FILE'S IDS.
//
// Owner, 2026-10-23: on a multi-file model (main + linked), clicking one
// file's parent row selected BOTH trees. The structural ids are `$id`, the
// serializer's reference number, and it restarts at "1" in every file - so
// the main file's root and the linked file's root were both "1", and so were
// their first categories and groups. The page keeps ONE selectGroups and ONE
// set of open keys for all the file trees, so a row selected or opened in one
// file lit up its twin in the other.
//
// Every node gets the file's scope in front of its id - roots, categories,
// groups, lazy rows, placeholders AND LEAVES. A leaf's id is its $id too, so
// the main file's element "4" and the linked file's element "4" were the
// same row id: a 3D click on a linked element could open and scroll to the
// main file's row (the page scrolls with getElementById, which takes the
// first), and the pick could match the main file's leaf by $id before it
// reached the linked one by UniqueId. Selection still goes by UniqueId
// (meshInfo); helpers/three findRowForRecord asks for the UniqueId first and
// reads a scoped id back to its $id with unscopedNodeId.
// "#" because the tree library joins keys with "/".
export function scopedNodeId(scope, id) {
  if (!scope || id === undefined || id === null) return id;
  const v = String(id);
  const prefix = `${scope}#`;
  return v.startsWith(prefix) ? v : `${prefix}${v}`;
}

// The id as the export wrote it: "p:0#4" -> "4".
export function unscopedNodeId(id) {
  if (id === undefined || id === null) return id;
  const v = String(id);
  const at = v.indexOf("#");
  return at === -1 ? v : v.slice(at + 1);
}

// Scope a built tree (the [root] array every builder returns) in place and
// return it. Idempotent: a tree scoped twice keeps the same ids.
export function scopeTreeIds(tree, scope) {
  if (!scope || !Array.isArray(tree) || !tree[0]) return tree;
  (function walk(node, parent) {
    if (!node) return;
    node.id = scopedNodeId(scope, node.id);
    node.key = scopedNodeId(scope, node.key);
    node.parentId = parent ? parent.id : node.id;
    (node.nodes || []).forEach((child) => walk(child, node));
  })(tree[0], null);
  return tree;
}

// THE SAME FILE, FOR A SAVED FILTER RULE. A rule stores the root row it was
// made on; rules saved before 2026-10-23 hold the bare $id ("1"), which every
// file's root used to share. A scoped id matches only itself; a bare one
// matches the root with that $id AND that file name - so an old filter keeps
// working and still lands on the file it was made on, not on its twin.
export function sameTreeRoot(node, rule) {
  if (!node || !rule) return false;
  if (String(node.id) === String(rule.id)) return true;
  if (rule.id === undefined || rule.id === null || String(rule.id).includes("#")) return false;
  return unscopedNodeId(node.id) === String(rule.id) && (node.label || "") === (rule.label || "");
}
